/**
 * UrensaldoMutationsView — Chronologische weergave van alle potje-mutaties
 * voor één medewerker. Toont bron, reden, saldo-na-mutatie. Eigenaren
 * kunnen een handmatige correctie toevoegen via CorrectieMutationDialog.
 */

import { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../../stores/workforce-store';
import { formatHours } from '../../lib/hours-calculator';
import { calculatePotjeSaldo, type Mutation } from '../../lib/urensaldo-calculator';
import { CorrectieMutationDialog } from './CorrectieMutationDialog';

interface Props {
  staffId: string;
  staffName: string;
  canEdit?: boolean;
}

const SOURCE_META: Record<string, { label: string; className: string }> = {
  nmbrs_export: { label: 'NMBRS', className: 'bg-emerald-50 text-emerald-700' },
  manual_correction: { label: 'Handmatig', className: 'bg-indigo-50 text-indigo-700' },
  migration: { label: 'Migratie', className: 'bg-slate-100 text-slate-600' },
  end_of_contract: { label: 'Uit dienst', className: 'bg-orange-50 text-orange-700' },
};

export function UrensaldoMutationsView({ staffId, staffName, canEdit = true }: Props) {
  const [mutations, setMutations] = useState<Mutation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [yearFilter, setYearFilter] = useState<number | 'all'>('all');
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data: Mutation[] = await apiFetch(`/api/workforce/urensaldo-mutations?staff_id=${staffId}`);
      setMutations(data || []);
    } catch (e) {
      setError((e as Error).message || 'Laden mislukt.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [staffId]);

  const years = useMemo(() => {
    const set = new Set<number>();
    for (const m of mutations) {
      if (m.created_at) set.add(new Date(m.created_at).getFullYear());
    }
    return Array.from(set).sort((a, b) => b - a);
  }, [mutations]);

  const filtered = useMemo(() => {
    if (yearFilter === 'all') return mutations;
    return mutations.filter(m => m.created_at && new Date(m.created_at).getFullYear() === yearFilter);
  }, [mutations, yearFilter]);

  // Chronologisch oud → nieuw voor saldo-berekening
  const chronological = useMemo(
    () => [...filtered].sort((a, b) => (a.created_at || '').localeCompare(b.created_at || '')),
    [filtered]
  );

  const withRunningBalance = useMemo(() => {
    let running = 0;
    return chronological.map(m => {
      running += Number(m.hours);
      return { ...m, runningBalance: running };
    });
  }, [chronological]);

  const current = calculatePotjeSaldo(mutations);

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-main">Potje & mutaties</h3>
          <p className="text-xs text-muted">
            Actueel saldo:{' '}
            <span className={`font-semibold ${current >= 0 ? 'text-indigo-600' : 'text-red-600'}`}>
              {current >= 0 ? '+' : ''}{formatHours(current)}
            </span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          {years.length > 1 && (
            <select
              value={yearFilter}
              onChange={e => setYearFilter(e.target.value === 'all' ? 'all' : Number(e.target.value))}
              className="rounded-lg border border-gray-200 px-2 py-1 text-xs"
            >
              <option value="all">Alle jaren</option>
              {years.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          )}
          {canEdit && (
            <button
              type="button"
              onClick={() => setDialogOpen(true)}
              className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700"
            >
              + Correctie
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>
      )}

      {loading ? (
        <p className="py-6 text-center text-sm text-muted">Laden...</p>
      ) : withRunningBalance.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted">Nog geen mutaties.</p>
      ) : (
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50 text-xs uppercase text-muted">
              <th className="px-3 py-2">Datum</th>
              <th className="px-3 py-2">Periode</th>
              <th className="px-3 py-2 text-right">Mutatie</th>
              <th className="px-3 py-2">Bron</th>
              <th className="px-3 py-2">Reden</th>
              <th className="px-3 py-2 text-right">Saldo na</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {[...withRunningBalance].reverse().map(m => {
              const meta = SOURCE_META[m.source] || { label: m.source, className: 'bg-gray-100 text-gray-600' };
              const hours = Number(m.hours);
              return (
                <tr key={m.id} className={m.voided_at ? 'opacity-50 line-through' : ''}>
                  <td className="px-3 py-2 text-xs text-muted">
                    {m.created_at ? m.created_at.slice(0, 10) : '-'}
                  </td>
                  <td className="px-3 py-2 text-xs text-muted capitalize">{m.period_label || '-'}</td>
                  <td className={`px-3 py-2 text-right font-mono font-semibold ${hours >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {hours >= 0 ? '+' : ''}{hours.toFixed(2)}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`inline-block rounded px-2 py-0.5 text-[10px] font-medium ${meta.className}`}>
                      {meta.label}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs text-main">{m.reason}</td>
                  <td className="px-3 py-2 text-right font-mono text-xs text-muted">
                    {(m as Mutation & { runningBalance: number }).runningBalance.toFixed(2)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <CorrectieMutationDialog
        open={dialogOpen}
        staffId={staffId}
        staffName={staffName}
        onClose={() => setDialogOpen(false)}
        onSaved={load}
      />
    </div>
  );
}
